import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import cn from "classnames";
import styles from "./PostcardHero.module.sass";
import { Link, useNavigate } from "react-router-dom";
import Form from "../Form";

const POSTAWAY_INTRO_VIDEO_URL =
  process.env.REACT_APP_POSTAWAY_INTRO_VIDEO_URL ||
  'https://plxuc9elglrdwhhs.public.blob.vercel-storage.com/jamie_01.mp4';

const JoinCommunityButton = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={ref} className={styles.joinCommunityWrap}>
      <button
        type="button"
        className={styles.joinCommunityBtn}
        onClick={() => setOpen((prev) => !prev)}
      >
        JOIN OUR COMMUNITY
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ marginLeft: 8 }}>
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && (
        <div className={styles.joinCommunityDropdown}>
          <Link to="/creator-signup" className={styles.joinCommunityItem} onClick={() => setOpen(false)}>Creator</Link>
          <Link to="/host-select" className={styles.joinCommunityItem} onClick={() => setOpen(false)}>Host</Link>
          <Link to="/traveler-signup" className={styles.joinCommunityItem} onClick={() => setOpen(false)}>Traveler</Link>
          <Link to="/partner-signup" className={styles.joinCommunityItem} onClick={() => setOpen(false)}>Preferred Partner</Link>
        </div>
      )}
    </div>
  );
};

const PostcardHero = ({ className, centerTitle, centerSubTitle, centerBody, centerContent, centerImageSrc, centerImageAlt, belowHeader, belowTitle, belowTitleCenter, belowContent, hideGuides, hideTagline, showDividerOnly, pushDown, pushDownLight, tall, homeLayout, brandHeader, brandHeaderAddon, disableStamps, email, setEmail, onSubmit, submitting, ok, customMinHeight }) => {
  const innerRef = useRef(null);
  const [isInside, setIsInside] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [stamps, setStamps] = useState([]);
  const [stampImages, setStampImages] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [pendingWidth, setPendingWidth] = useState(48);
  const [pendingRotate, setPendingRotate] = useState(0);
  const [hasWrapped, setHasWrapped] = useState(false);
  const prevIdxRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 1, height: 1 });
  const [storedEmail, setStoredEmail] = useState("");
  const [redPostmarkLoaded, setRedPostmarkLoaded] = useState(false);
  const [redPostmarkError, setRedPostmarkError] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const homeVideoRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const base = "/bloom/lovable-uploads/";
    const maxToProbe = 30;
    const stopAfterConsecutiveMisses = 3;
    let misses = 0;
    const loaded = [];

    const tryLoad = (idx) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ ok: true, w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve({ ok: false });
        img.src = `${base}${idx}.png`;
      }).then((res) => {
        if (res.ok && res.w && res.h) {
          loaded.push({ src: `${base}${idx}.png`, naturalWidth: res.w, naturalHeight: res.h });
          misses = 0;
        } else {
          misses += 1;
        }
        return res.ok;
      });

    (async () => {
      for (let i = 1; i <= maxToProbe; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await tryLoad(i);
        if (misses >= stopAfterConsecutiveMisses && i > 3) break;
      }
      if (mounted) setStampImages(loaded);
    })();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    try {
      const e = typeof window !== 'undefined' ? (window.localStorage.getItem('postaway_email') || '') : '';
      setStoredEmail(e);
    } catch (_) {}
  }, []);

  // Preload red postmark image to prevent Safari rendering issues
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setRedPostmarkLoaded(true);
    };
    img.onerror = () => {
      console.error('Failed to preload red postmark stamp');
      setRedPostmarkError(true);
    };
    img.src = "/bloom/lovable-uploads/6729e59e-5c9c-4686-b2f9-416b4ff21809.png";
  }, []);

  useEffect(() => {
    const updateSize = () => {
      if (!innerRef.current) return;
      const r = innerRef.current.getBoundingClientRect();
      setContainerSize({ width: r.width, height: r.height });
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  useEffect(() => {
    // Detect wrap-around (index decreasing)
    if (prevIdxRef.current !== null && currentIdx < prevIdxRef.current) {
      setHasWrapped(true);
    }
    prevIdxRef.current = currentIdx;

    if (!stampImages.length) return;
    const meta = stampImages[currentIdx % stampImages.length];
    const natural = meta.naturalWidth;
    const minDisplay = Math.max(24, Math.min(48, Math.floor(natural * 0.25)));
    const fileName = ((meta.src || "").split('/').pop()) || "";
    const isAlwaysFull = fileName === "2.png";
    // After first full cycle, skip 2.png entirely
    if (hasWrapped && isAlwaysFull) {
      setCurrentIdx((idx) => (idx + 1) % stampImages.length);
      return;
    }
    const cap = isAlwaysFull ? 600 : 128;
    const maxDisplay = Math.min(natural, cap);
    if (isAlwaysFull) {
      setPendingWidth(maxDisplay);
      setPendingRotate(0);
    } else {
      const scale = 0.6 + Math.random() * 0.4; // 60% - 100%
      const width = Math.round(Math.min(maxDisplay, Math.max(minDisplay, maxDisplay * scale)));
      setPendingWidth(width);
      const rot = Math.round(-25 + Math.random() * 50);
      setPendingRotate(rot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, stampImages.length, hasWrapped]);

  const handleMouseMove = (e) => {
    if (!innerRef.current) return;
    const rect = innerRef.current.getBoundingClientRect();
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    if (rect.width !== containerSize.width || rect.height !== containerSize.height) {
      setContainerSize({ width: rect.width, height: rect.height });
    }
  };

  const handleClick = (e) => {
    if (!innerRef.current || !stampImages.length) return;
    const rect = innerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const meta = stampImages[currentIdx % stampImages.length];
    const fileName = ((meta.src || "").split('/').pop()) || "";
    const isAlwaysFull = fileName === "2.png";
    const widthForStamp = isAlwaysFull ? Math.min(meta.naturalWidth, 600) : pendingWidth;
    const rotateForStamp = isAlwaysFull ? 0 : pendingRotate;
    const xPct = rect.width ? x / rect.width : 0;
    const yPct = rect.height ? y / rect.height : 0;
    const wPct = rect.width ? Math.min(0.95, widthForStamp / rect.width) : 0;
    setStamps((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        src: meta.src,
        // percentage-based positioning and sizing for responsiveness
        xPct,
        yPct,
        wPct,
        rotateDeg: rotateForStamp,
      },
    ]);
    setCurrentIdx((idx) => (idx + 1) % stampImages.length);
  };

  const currentMeta = stampImages.length ? stampImages[currentIdx % stampImages.length] : null;
  const cursorIsAlwaysFull = currentMeta ? ((((currentMeta.src || "").split('/').pop()) || "") === "2.png") : false;
  const cursorWidthPx = currentMeta ? (cursorIsAlwaysFull ? Math.min(currentMeta.naturalWidth, 600) : pendingWidth) : pendingWidth;
  const cursorWidthPct = containerSize.width ? (cursorWidthPx / containerSize.width) * 100 : 0;
  const cursorRotate = cursorIsAlwaysFull ? 0 : pendingRotate;

  const navigate = useNavigate();
  
  // Check authentication status
  const isLoggedIn = typeof window !== 'undefined' ? !!window.localStorage.getItem('postaway_email') : false;
  const userType = typeof window !== 'undefined' ? window.localStorage.getItem('postaway_user_type') || '' : '';

  // Helper function to route logged-in users to their dashboard
  const routeToLoggedInDashboard = () => {
    if (userType === 'creator') {
      navigate('/collab-board');
    } else if (userType === 'host') {
      navigate('/host-dashboard');
    } else if (userType === 'traveler') {
      navigate('/travel-board');
    }
  };

  // Handler for Creator button
  const handleCreatorClick = (e) => {
    e.preventDefault();
    if (isLoggedIn) {
      routeToLoggedInDashboard();
    } else {
      navigate('/creator-login');
    }
  };

  // Handler for Host button
  const handleHostClick = (e) => {
    e.preventDefault();
    if (isLoggedIn) {
      routeToLoggedInDashboard();
    } else {
      navigate('/host-select');
    }
  };

  // Handler for Traveler button
  const handleTravelerClick = (e) => {
    e.preventDefault();
    if (isLoggedIn) {
      routeToLoggedInDashboard();
    } else {
      navigate('/traveler-signup');
    }
  };

  return (
    <>
    <div className={cn(styles.border, className, { [styles.homeFixed]: homeLayout })}>
      <img
        src="/bloom/lovable-uploads/1bacafd6-9c8c-4d9b-bba4-227fec600409.png"
        alt="Large circular postmark stamp"
        className={styles.postmarkBig}
      />
      <div
        ref={innerRef}
        className={cn(styles.inner, { [styles.innerHome]: homeLayout, [styles.onlyDivider]: showDividerOnly, [styles.hasBelow]: Boolean(belowContent), [styles.storyTall]: tall })}
        onMouseEnter={() => !homeLayout && !disableStamps && setIsInside(true)}
        onMouseLeave={() => !homeLayout && !disableStamps && setIsInside(false)}
        onMouseMove={!homeLayout && !disableStamps ? handleMouseMove : undefined}
        onClick={!homeLayout && !disableStamps ? handleClick : undefined}
        style={{ cursor: stampImages.length && !homeLayout && !disableStamps ? "none" : "default", ...(customMinHeight ? { minHeight: customMinHeight } : {}) }}
      >
        <div className={styles.header}>
          {(homeLayout || brandHeader) ? (
            <>
            <Link to="/" onClick={(e) => e.stopPropagation()} className={cn(styles.brandHeader, { [styles.brandHome]: homeLayout })} aria-label="Go to home">
              <div className={styles.textLogo}>postaway<sup className={styles.tm}>TM</sup></div>
              <div className={styles.textTagline}>SEARCH. STAY. SHARE.</div>
              {homeLayout && (
                <div className={styles.textSubtext}>Postaway is the preferred hospitality collaboration headquarters for creators, hosts and travelers to connect, collaborate and build community.</div>
              )}
            </Link>
            {homeLayout && <JoinCommunityButton />}
            </>
          ) : (
            <>
              <div className={styles.logoWrap}>
                <Link to="/" onClick={(e) => e.stopPropagation()} aria-label="Go to home">
                  <img
                    src="/bloom/lovable-uploads/aecbff76-39a9-4c8b-907f-17f41a3db1de.png"
                    alt="Travel with Postaway Logo"
                    className={styles.logo}
                  />
                </Link>
              </div>
              <h1 className={styles.title}>Travel with Postaway</h1>
              <div className={styles.wavesCenter}>
                <img
                  src="/bloom/lovable-uploads/f5701bae-3140-440e-a0f5-f1010d27cd19.png"
                  alt="Decorative postmark waves"
                />
              </div>
            </>
          )}
          {(homeLayout || brandHeader) && brandHeaderAddon && (
            <div className={styles.brandHeaderAddon}>{brandHeaderAddon}</div>
          )}
        </div>

        {belowContent && (
          <div className={styles.belowContent}>
            {belowHeader && (<div className={styles.belowHeader}>{belowHeader}</div>)}
            {belowTitle && <h2 className={cn(styles.belowTitle, { [styles.belowTitleCenter]: belowTitleCenter })}>{belowTitle}</h2>}
            {belowContent}
          </div>
        )}

        {/* removed vintage corner stamp; replaced with square placeholder + logo overlay */}
        {!redPostmarkError && (
          <img
            src="/bloom/lovable-uploads/6729e59e-5c9c-4686-b2f9-416b4ff21809.png"
            alt="Red postmark stamp"
            className={cn(styles.redPostmark, { [styles.redPostmarkLoaded]: redPostmarkLoaded })}
            loading="eager"
            decoding="async"
            onLoad={() => setRedPostmarkLoaded(true)}
            onError={(e) => {
              console.error('Failed to load red postmark stamp:', e);
              setRedPostmarkError(true);
            }}
          />
        )}

        {homeLayout && (
          <div className={styles.cancelWaves}>
            <img src="/bloom/lovable-uploads/f5701bae-3140-440e-a0f5-f1010d27cd19.png" alt="cancellation lines" />
          </div>
        )}

        {!hideTagline && !homeLayout && (
          <div className={styles.tagline}>SEARCH. STAY. SHARE.</div>
        )}

        {/* Postcard guide lines */}
        {showDividerOnly || homeLayout ? (
          <div className={styles.guides}>
            <div className={cn(styles.divider, { [styles.dividerHome]: homeLayout })}></div>
          </div>
        ) : (
          !hideGuides && (
            <div className={styles.guides}>
              <div className={styles.divider}></div>
              <div className={styles.address}>
                <div className={styles.addrLine}></div>
                <div className={styles.addrLine}></div>
                <div className={styles.addrLine}></div>
                <div className={styles.addrLine}></div>
              </div>
            </div>
          )
        )}

        {(homeLayout) && (
          <div className={styles.homeContent}>
            <div className={styles.leftCol}>
              <button 
                type="button" 
                className={styles.audBtn} 
                aria-label="Content Creators"
                onClick={handleCreatorClick}
              >
                <div className={styles.audTitle}>CONTENT CREATORS</div>
              </button>
              <div className={styles.audNote}>INFLUENCERS • PHOTOGRAPHERS • UGC • TALENT AGENCIES</div>
              <button 
                type="button" 
                className={cn(styles.audBtn, styles.audBtnRed)} 
                aria-label="Hosts"
                onClick={handleHostClick}
              >
                <div className={styles.audTitle}>HOSTS</div>
              </button>
              <div className={styles.audNote}>VACATION RENTAL HOSTS • BOUTIQUE HOTELS • TOURISM BOARDS</div>
            </div>
            <div className={styles.rightCol}>
              <button 
                type="button" 
                className={cn(styles.audBtn, styles.audBtnOutline)} 
                aria-label="Travelers"
                onClick={handleTravelerClick}
              >
                <div className={styles.audTitle}>TRAVELERS</div>
              </button>
              <div className={styles.audNote}>ANYONE WITH A SUITCASE</div>
              <div className={styles.waitlist}>
                <div className={styles.wlFormWrap}>
                  <Form
                    big
                    className={styles.wlForm}
                    value={email}
                    setValue={setEmail}
                    onSubmit={onSubmit}
                    placeholder="Enter your email"
                    type="email"
                    name="email"
                    icon={ok ? "check" : "arrow-next"}
                    success={ok}
                    disabled={submitting}
                  />
                </div>
                <div className={styles.wlHeadline}>NOW BOARDING!</div>
                <div className={styles.wlSub}>Join our Newsletter</div>
              </div>
            </div>
          </div>
        )}

        {homeLayout && (
          <div className={styles.homeTestimonials}>
            {[{
              img: '/chloe-creator.png',
              name: 'Chloe, Creator',
              quote: '\u201CAs a travel content creator, I\u2019ve worked with a lot of vacation rental hosts \u2014 and keeping track of pitches, approvals, comped stays, deliverables, and posting dates used to be a total headache. Using Postaway\u2019s \u201CMy Collab Tracker\u201D feature made the entire collaboration process incredibly smooth.\u201D'
            }, {
              img: '/jordan.png',
              name: 'Jordan, Host',
              quote: '\u201CPostaway made finding the right creator to collaborate with incredibly easy. Instead of spending hours searching and vetting influencers, I was matched with a quality content creator whose audience aligned perfectly with my property. The process was smooth, communication was clear, and we saw a noticeable boost in awareness.\u201D'
            }, {
              img: '/hayden.png',
              name: 'Hayden, Traveler',
              quote: '\u201CI was overwhelmed planning my New Zealand trip, but Postaway made it effortless. I found the perfect itinerary tailored to exactly what I wanted \u2014 adventure, scenery, and unique stays all in one seamless plan. It saved me hours of research and made me even more excited for the trip.\u201D'
            }].map((t, i) => (
              <div key={i} className={styles.testimonialCard}>
                <img src={t.img} alt={t.name} className={styles.testimonialImg} />
                <div className={styles.testimonialBody}>
                  <div className={styles.testimonialName}>{t.name}</div>
                  <div className={styles.testimonialQuote}>{t.quote}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {homeLayout && (
          <div className={styles.watchVideoRow}>
            <button
              type="button"
              className={styles.watchVideoBtn}
              onClick={() => setShowVideoModal(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                <polygon points="5,3 19,12 5,21" fill="currentColor" />
              </svg>
              Watch: What is Postaway
            </button>
          </div>
        )}

        {(centerTitle || centerBody || centerContent || centerImageSrc) && !homeLayout && (
          <div className={cn(styles.centerText, { [styles.pushedDown]: pushDown, [styles.pushedDownLight]: pushDownLight })}>
            {centerTitle && <h2 className={styles.centerTitle}>{centerTitle}</h2>}
            {centerImageSrc && (
              <img src={centerImageSrc} alt={centerImageAlt || ""} className={styles.centerImage} />
            )}
            {centerSubTitle && <h3 className={styles.centerSubTitle}>{centerSubTitle}</h3>}
            {Array.isArray(centerBody) ? (
              <div className={styles.centerList}>
                {centerBody.map((line, idx) => (
                  <div key={idx}>{line}</div>
                ))}
              </div>
            ) : (
              centerBody && <p className={styles.centerBody}>{centerBody}</p>
            )}
            {centerContent && <div className={styles.centerContent}>{centerContent}</div>}
          </div>
        )}

        {/* Placed stamps */}
        <div className={styles.stampsLayer}>
          {stamps.map((s) => {
            const leftVal = typeof s.xPct === "number" ? `${s.xPct * 100}%` : s.x;
            const topVal = typeof s.yPct === "number" ? `${s.yPct * 100}%` : s.y;
            const widthVal = typeof s.wPct === "number" ? `${s.wPct * 100}%` : s.width;
            return (
              <img
                key={s.id}
                src={s.src}
                alt="stamp"
                className={styles.stamped}
                style={{ left: leftVal, top: topVal, width: widthVal, transform: `translate(-50%, -50%) rotate(${s.rotateDeg}deg)` }}
              />
            );
          })}
        </div>

        {/* Cursor preview */}
        {!homeLayout && !disableStamps && isInside && stampImages.length > 0 && (
          <img
            src={stampImages[currentIdx % stampImages.length].src}
            alt="cursor-stamp"
            className={styles.cursorStamp}
            style={{ left: mousePos.x, top: mousePos.y, width: `${cursorWidthPct}%`, transform: `translate(-50%, -50%) rotate(${cursorRotate}deg)` }}
          />
        )}

        {/* Stamp placeholder outline */}
        <div className={styles.stampOutline}></div>
        <img
          src="/images/postaway%20logo%201.svg"
          alt="Postaway stamp"
          className={styles.stampLogo}
        />

        <img
          src="/bloom/lovable-uploads/a27b7337-5a1a-4df4-b3cd-c792eb164d6b.png"
          alt="RSVP stamp"
          className={styles.rsvpStamp}
        />

      </div>
    </div>
    {/* Luggage tags below the postcard */}
    <div className={styles.tagsRowBelow} aria-label="Postaway info tabs">
      <Link to="/what-is-postaway" className={cn(styles.tag, styles.mint)}>
        <span className={styles.tagTitle}>What is Postaway</span>
      </Link>
      <Link to="/why-postaway" className={cn(styles.tag, styles.pink)}>
        <span className={styles.tagTitle}>Why Postaway</span>
      </Link>
      <Link to="/how-to-use-postaway" className={cn(styles.tag, styles.cream)}>
        <span className={styles.tagTitle}>How to use Postaway</span>
      </Link>
      <Link to="/our-story" className={cn(styles.tag, styles.tan)}>
        <span className={styles.tagTitle}>Our Story</span>
      </Link>
      <Link to="/contact" className={cn(styles.tag, styles.olive)}>
        <span className={styles.tagTitle}>Contact</span>
      </Link>
    </div>
    {/* Social icons below luggage tags */}
    <div className={styles.socialIconsContainer}>
      <a 
        href="https://www.instagram.com/postawaytravels/" 
        target="_blank" 
        rel="noopener noreferrer" 
        className={cn(styles.socialIcon, styles.socialIconClickable)}
        aria-label="Instagram"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.98-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.98-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" 
            fill="#0f084b"
          />
        </svg>
      </a>
      <div className={styles.socialIcon} aria-label="TikTok">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" 
            fill="#0f084b"
          />
        </svg>
      </div>
      <div className={styles.socialIcon} aria-label="Facebook">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" 
            fill="#0f084b"
          />
        </svg>
      </div>
      <div className={styles.socialIcon} aria-label="X (Twitter)">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" 
            fill="#0f084b"
          />
        </svg>
      </div>
    </div>
    {showVideoModal && createPortal(
      <div
        className={styles.videoOverlay}
        onClick={() => {
          setShowVideoModal(false);
          if (homeVideoRef.current) { homeVideoRef.current.pause(); }
        }}
      >
        <div className={styles.videoExpanded} onClick={(e) => e.stopPropagation()}>
          <button
            className={styles.videoCloseBtn}
            onClick={() => {
              setShowVideoModal(false);
              if (homeVideoRef.current) { homeVideoRef.current.pause(); }
            }}
            aria-label="Close video"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
          <video
            ref={homeVideoRef}
            className={styles.videoPlayer}
            src={POSTAWAY_INTRO_VIDEO_URL}
            playsInline
            controls
          />
        </div>
      </div>,
      document.body
    )}
    </>
  );
};

export default PostcardHero;


